import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, Award } from "lucide-react";
import { LeadForm } from "@/components/site/LeadForm";
import { usePartner } from "@/context/PartnerContext";

export const Hero = () => {
  const { config } = usePartner();
  return (
    <section
      id="top"
      className={`relative overflow-hidden ${config ? "pt-32 sm:pt-28" : "pt-24 sm:pt-28"} lg:pt-28 lg:min-h-[calc(100vw*822/1914+8rem)] flex flex-col justify-center pb-8 sm:pb-12`}
    >
      {/* Fondo con imagen panorámica del rubro */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/generated/hero-bg.jpg')" }}
        aria-hidden="true"
      />
      {/* Overlay MUY sutil para conservar la vitalidad de la imagen */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/20" />

      {config && (
        <div data-testid="partner-benefit-ribbon" className="relative mx-auto max-w-7xl px-4 sm:px-8 w-full mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-3 sm:gap-4 rounded-2xl bg-brand-ink px-4 py-3 shadow-lg shadow-brand-ink/40 ring-1 ring-white/15">
            {!config.bannerHideLogos && config.logoWhite && (
              <>
                <img src={config.logoWhite} alt={config.name} className="h-7 sm:h-9 w-auto shrink-0" />
                <span className="h-7 w-px bg-white/25 shrink-0" />
              </>
            )}
            <p className="text-sm sm:text-base font-semibold leading-tight text-white text-center">
              {config.bannerText}
            </p>
          </div>
        </div>
      )}

      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-4 items-start">
        {/* CARD IZQUIERDO — texto en glass */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 rounded-3xl bg-white/80 backdrop-blur-md ring-1 ring-white/60 p-5 sm:p-6 shadow-xl shadow-brand-ink/10"
        >
          <span
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 rounded-full border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1 text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-brand-ink"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-brand-yellow" /> Corredora certificada CMF · Chile
          </span>

          <h1
            data-testid="hero-title"
            className="font-display mt-4 text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.05] tracking-tight text-brand-ink"
          >
            Seguros para tu <span className="text-brand-yellow">maquinaria</span>,{" "}
            equipos y tecnología.
          </h1>

          <p className="mt-4 max-w-xl text-sm sm:text-[15px] leading-relaxed text-gray-700">
            Cubrimos maquinaria agrícola, de construcción y minería, y equipos
            electrónicos de uso particular o comercial. Cotiza rápido y recibe una
            propuesta con la mejor cobertura y el respaldo de las principales aseguradoras.
          </p>

          <div className="mt-4 grid gap-2">
            <div className="flex items-start gap-2.5 rounded-xl border border-brand-yellow/20 bg-brand-yellow/5 px-3 py-2">
              <BadgeCheck className="h-5 w-5 shrink-0 text-brand-yellow mt-0.5" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-brand-ink">Daños físicos</span> de la
                maquinaria y del equipo asegurado.
              </p>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl border border-brand-yellow/20 bg-brand-yellow/5 px-3 py-2">
              <Award className="h-5 w-5 shrink-0 text-brand-yellow mt-0.5" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-brand-ink">Responsabilidad Civil</span> por
                daños corporales y/o materiales a terceros (maquinaria).
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] sm:text-xs font-medium text-gray-700 lg:flex-nowrap lg:overflow-hidden">
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><BadgeCheck className="h-3.5 w-3.5 shrink-0 text-brand-yellow" /> Sin costo de cotización</span>
            <span className="text-gray-300 hidden lg:inline" aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><BadgeCheck className="h-3.5 w-3.5 shrink-0 text-brand-yellow" /> Respuesta rápida</span>
            <span className="text-gray-300 hidden lg:inline" aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><BadgeCheck className="h-3.5 w-3.5 shrink-0 text-brand-yellow" /> 100% online</span>
          </div>
        </motion.div>

        {/* CARD DERECHO — formulario en glass (mismo estilo) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          id="cotizar"
          className="lg:col-start-7 lg:col-span-6 w-full scroll-mt-24"
        >
          <LeadForm />
        </motion.div>
      </div>
    </section>
  );
};
